import { useState } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import type { Patient } from "~/api/patients";
import { PaginatedTable } from "~/components/Shared/PaginatedTable";
import { PFAStatusBadge } from "~/components/Shared/PFAStatusBadge";
import { ROUTES } from "~/router";
import { DropdownMenu } from "~/ui";
import { EditPatientModal } from "../../EditPatientModal";

interface PatientsTableProps {
  patients: Patient[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (val: number) => void;
  totalItems?: number;
  pageSize: number;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
}

export const MODAL_TYPE_OPTIONS = {
  DELETE: "delete",
  EDIT: "edit",
} as const;

export type ModalType =
  (typeof MODAL_TYPE_OPTIONS)[keyof typeof MODAL_TYPE_OPTIONS];

export const PatientsTable = ({
  patients,
  isLoading,
  currentPage,
  setCurrentPage,
  totalItems,
  pageSize,
  sorting,
  setSorting,
}: PatientsTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState<Patient>();
  const [modalOpen, setModalOpen] = useState<ModalType>();

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setModalOpen(MODAL_TYPE_OPTIONS.EDIT);
  };

  const columns: ColumnDef<Patient>[] = [
    {
      header: t("patients.patients_table.full_name"),
      accessorKey: "name",
      cell: ({ row }) => {
        const patient = row.original;
        return (
          <Link
            to={ROUTES.patientDetail.replace(":id", patient.id.toString())}
            className="text-primary-40 underline dark:text-primary-80"
          >
            {patient.name} {patient.surname}
          </Link>
        );
      },
    },
    {
      id: "unit",
      header: t("patients.patients_table.unit"),
      accessorKey: "unit.name",
    },
    {
      id: "insole",
      header: t("patients.patients_table.insole"),
      accessorKey: "active_insole.serial_number",
    },
    {
      accessorKey: "last_test_score",
      header: t("patients.patients_table.last_updated_score"),
      cell: ({ row }) => {
        const lastPfa = row.original.last_physical_fitness_assessment_test;
        return lastPfa ? (
          <PFAStatusBadge status={lastPfa.status} />
        ) : (
          <span>—</span>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row: { original } }) => (
        <div className="flex justify-end">
          <DropdownMenu
            buttonAriaLabel={t(
              "patients.patients_table.view_options_for_name",
              {
                name: `${original.name} ${original.surname}`,
              },
            )}
            options={[
              {
                label: t("patients.view_patient_profile"),
                onClick: () =>
                  navigate(
                    ROUTES.patientDetail.replace(":id", original.id.toString()),
                  ),
              },
              {
                label: t("patients.edit_patient_details"),
                onClick: () => handleEditPatient(original),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PaginatedTable
        data={patients}
        columns={columns}
        isLoadingData={isLoading}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalRows={totalItems}
        sorting={sorting}
        setSorting={setSorting}
      />
      {selectedPatient && (
        <EditPatientModal
          isOpen={modalOpen === MODAL_TYPE_OPTIONS.EDIT}
          patient={selectedPatient}
          unit={selectedPatient.unit}
          onClose={() => setModalOpen(undefined)}
        />
      )}
    </>
  );
};
